import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Search, MailWarning, Webhook, Inbox, CalendarClock, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, INTEREST_OPTIONS, INTEREST_LABELS } from "../lib/api";
import Logo from "../components/Logo";

const STATUS_OPTIONS = ["new", "reviewing", "contacted", "closed"];
const STATUS_STYLES = {
  new: "bg-teal-soft text-teal-dark border-teal/40",
  reviewing: "bg-ice text-royal border-royal/30",
  contacted: "bg-emeraldx-soft text-emeraldx border-emeraldx/30",
  closed: "bg-ice text-slate-light border-line",
};

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-line bg-white p-5 shadow-card" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-ice"><Icon className="w-5 h-5 text-royal" /></span>
        <div>
          <p className="text-2xl font-display font-extrabold text-navy leading-none">{value}</p>
          <p className="text-[11px] font-bold uppercase tracking-wider2 text-slate-light mt-1">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/leads", { params: { status, category, q } });
      setLeads(data.leads);
      setStats(data.stats);
      setConfig(data.config);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin");
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  }, [status, category, q, navigate]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (leadId, next) => {
    setLeads((ls) => ls.map((l) => (l.lead_id === leadId ? { ...l, status: next } : l)));
    try {
      await api.patch(`/admin/leads/${leadId}`, { status: next });
      toast.success("Lead updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      load();
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    navigate("/admin");
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    } catch (_) { return iso; }
  };

  return (
    <main className="min-h-screen bg-ice/50" data-testid="page-admin-dashboard">
      {/* Top bar */}
      <header className="bg-navy-950 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo dark />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs font-semibold text-ice/50">Lead Review Workspace</span>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm font-bold border border-white/20 rounded-full px-4 py-2 hover:bg-white/10 transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Config banners */}
        {config && !config.email_notifications && (
          <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-5 flex items-start gap-3" data-testid="config-email-banner">
            <MailWarning className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed text-amber-900">
              <strong>Email notifications aren't configured yet.</strong> Leads are saved securely here, but the
              business owner isn't emailed. Add <code className="bg-amber-100 px-1.5 py-0.5 rounded">OWNER_EMAIL</code>{" "}
              (and keep <code className="bg-amber-100 px-1.5 py-0.5 rounded">EMERGENT_EMAIL_KEY</code>) to the backend
              environment to turn notifications on.
            </p>
          </div>
        )}
        {config && config.email_notifications && config.email_failures_7d > 0 && (
          <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-5 flex items-start gap-3" data-testid="config-email-failure-banner">
            <MailWarning className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed text-amber-900">
              <strong>Email delivery is failing.</strong> Every inquiry is being emailed to{" "}
              <code className="bg-amber-100 px-1.5 py-0.5 rounded">admin@communitypromotionsllc.com</code>, but that
              mailbox can't receive mail yet — the domain has no email service (MX records) set up. Leads are stored
              safely here; delivery starts automatically once the mailbox is active.
            </p>
          </div>
        )}
        {config && !config.crm_delivery && (
          <div className="mb-6 rounded-2xl border border-line bg-white p-5 flex items-start gap-3" data-testid="config-crm-banner">
            <Webhook className="w-5 h-5 text-royal shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed text-slate">
              <strong>CRM delivery (optional):</strong> set{" "}
              <code className="bg-ice px-1.5 py-0.5 rounded">CRM_WEBHOOK_URL</code> in the backend environment to push
              every new lead to your CRM endpoint. Until then, leads remain safely in this dashboard.
            </p>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={FolderOpen} label="Total inquiries" value={stats.total} testid="stat-total-leads" />
            <StatCard icon={Inbox} label="New" value={stats.by_status.new} testid="stat-new-leads" />
            <StatCard icon={CalendarClock} label="Last 7 days" value={stats.last_7_days} testid="stat-recent-leads" />
            <StatCard icon={Search} label="Contacted" value={stats.by_status.contacted} testid="stat-contacted-leads" />
          </div>
        )}

        {/* Filters */}
        <div className="rounded-2xl border border-line bg-white p-4 mb-6 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-light" />
            <input
              data-testid="admin-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search business, name, email…"
              className="w-full rounded-xl border border-line pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/60"
            />
          </div>
          <select data-testid="admin-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-line px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal/60">
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <select data-testid="admin-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-xl border border-line px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal/60">
            <option value="">All categories</option>
            {INTEREST_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Category chips */}
        {stats && (
          <div className="mb-6 flex flex-wrap gap-2">
            {Object.entries(stats.by_category).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-3.5 py-1.5 text-xs font-bold text-slate">
                {INTEREST_LABELS[k]} <span className="text-teal-dark">{v}</span>
              </span>
            ))}
          </div>
        )}

        {/* Leads table */}
        <div className="rounded-2xl border border-line bg-white shadow-card overflow-x-auto" data-testid="admin-leads-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-[11px] uppercase tracking-wider2 text-slate-light">
                <th className="px-5 py-4 font-bold">Received</th>
                <th className="px-5 py-4 font-bold">Business</th>
                <th className="px-5 py-4 font-bold">Contact</th>
                <th className="px-5 py-4 font-bold">Interested in</th>
                <th className="px-5 py-4 font-bold">Size</th>
                <th className="px-5 py-4 font-bold">Source</th>
                <th className="px-5 py-4 font-bold">Delivery</th>
                <th className="px-5 py-4 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="8" className="px-5 py-10 text-center text-slate">Loading leads…</td></tr>
              )}
              {!loading && leads.length === 0 && (
                <tr><td colSpan="8" className="px-5 py-10 text-center text-slate" data-testid="leads-empty-state">No inquiries match these filters yet.</td></tr>
              )}
              {leads.map((l) => (
                <Fragment key={l.lead_id}>
                  <tr
                    data-testid="admin-leads-table-row"
                    onClick={() => setExpanded(expanded === l.lead_id ? null : l.lead_id)}
                    className="border-b border-line/70 hover:bg-ice/40 cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-4 whitespace-nowrap text-slate">{fmtDate(l.created_at)}</td>
                    <td className="px-5 py-4 font-bold text-navy">{l.business_name}</td>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-navy">{l.contact_name}</p>
                      <p className="text-xs text-slate">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {(l.categories || []).map((c) => (
                          <span key={c} className="rounded-full bg-ice border border-royal/20 px-2.5 py-1 text-[11px] font-bold text-royal">{c}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-slate whitespace-nowrap">{l.business_size}</td>
                    <td className="px-5 py-4 text-xs text-slate whitespace-nowrap">{l.source_page}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold border w-fit ${l.notification_status === "sent" ? "bg-emeraldx-soft text-emeraldx border-emeraldx/30" : l.notification_status === "not_configured" ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-ice text-slate border-line"}`}>
                          email: {l.notification_status}
                        </span>
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold border w-fit ${l.crm_status === "sent" ? "bg-emeraldx-soft text-emeraldx border-emeraldx/30" : "bg-ice text-slate border-line"}`}>
                          crm: {l.crm_status}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                      <select
                        data-testid={`admin-lead-status-${l.lead_id}`}
                        value={l.status}
                        onChange={(e) => updateStatus(l.lead_id, e.target.value)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-teal/60 ${STATUS_STYLES[l.status] || "bg-ice text-slate border-line"}`}
                      >
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    </td>
                  </tr>
                  {expanded === l.lead_id && (
                    <tr key={`${l.lead_id}-detail`}>
                      <td colSpan="8" className="bg-ice/40 px-5 py-4" data-testid="lead-detail-row">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-[11px] font-bold uppercase tracking-wider2 text-slate-light">Business type</p>
                            <p className="mt-1 text-navy font-semibold">{l.business_type}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-bold uppercase tracking-wider2 text-slate-light">Comments</p>
                            <p className="mt-1 text-navy">{l.comments || "—"}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-bold uppercase tracking-wider2 text-slate-light">Reference</p>
                            <p className="mt-1 text-navy font-mono text-xs">{l.lead_id}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
